import React from 'react';
import { Card, Typography, Box, CircularProgress, Grid, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HeroSection from '../components/elements/HeroSection';
import useEventGroups from '../hooks/events/useEventGroups';
import { formatDate } from '../utils';
import { EventCategory, categoryIcons, categoryColors } from '../utils/categories';
import calendarIcon from '../assets/calendar.png';
import pencilIcon from '../assets/pencil.png';
import familyIcon from '../assets/family.png';
import useSettings from '../hooks/useSettings';
import { EVENT_GROUP_IMAGE_WIDTH, EVENTS_PAGE_MAX_WIDTH, fixedAspectRatioImageSx } from '../constants/layout';

// Color palette for event groups
const eventGroupColors = [
  '#2196F3', // blue
  '#4CAF50', // green  
  '#9C27B0', // purple
  '#FF9800', // orange
  '#00BCD4', // cyan
  '#E91E63', // pink
  '#3F51B5', // indigo
  '#FFC107', // amber
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { eventGroups, loading } = useEventGroups();
  const { heroBackground } = useSettings() as { heroBackground?: string };

  const handleGroupClick = (group: any) => {
    // Navigate to events page with group selected
    const searchParams = new URLSearchParams();
    searchParams.set('group_id', group.id);
    navigate(`/events?${searchParams.toString()}`);
  };

  const getGroupColor = (index: number) => {
    return eventGroupColors[index % eventGroupColors.length];
  };

  return (
    <>
      {/* Hero Section background (client setting type 14 if present, else default purple gradient) */}
      <Box
        sx={{
          background: heroBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
          }}
        >
          <HeroSection />
        </Box>
      </Box>

      {/* Event Groups Section */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          px: { xs: 2, md: 4 },
          backgroundColor: '#f5f5f5',
          minHeight: '60vh',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ maxWidth: EVENTS_PAGE_MAX_WIDTH, mx: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : eventGroups.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No events available.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
              {eventGroups.map((group: any, index: number) => {
                const groupColor = getGroupColor(index);

                return (
                  <Card
                    key={group.id}
                    onClick={() => handleGroupClick(group)}
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      boxShadow: 4,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { md: 'flex-start' },
                      overflow: 'hidden',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-4px)',
                      },
                    }}
                    elevation={2}
                  >
                    {/* Group image — fixed 16:9 panel (same ratio as event cards) */}
                    <Box
                      sx={{
                        width: { xs: '100%', md: EVENT_GROUP_IMAGE_WIDTH },
                        minWidth: { md: EVENT_GROUP_IMAGE_WIDTH },
                        maxWidth: { md: EVENT_GROUP_IMAGE_WIDTH },
                        ...fixedAspectRatioImageSx,
                        backgroundColor: groupColor,
                        backgroundImage: group.signedUrl ? `url(${group.signedUrl})` : 'none',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: group.signedUrl ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                        },
                      }}
                    />

                    {/* Group information — fills remaining space */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: { xs: 2, md: 2.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                          fontWeight: 700,
                          color: '#1a1a1a',
                          mb: 1,
                          fontSize: { xs: '1.5rem', md: '2rem' },
                          lineHeight: 1.2,
                        }}
                      >
                        {group.name}
                      </Typography>

                      {group.description && (
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#4a4a4a',
                            lineHeight: 1.5,
                            fontSize: { xs: '0.85rem', md: '0.9rem' },
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {group.description}
                        </Typography>
                      )}

                      {/* Compact Info Grid */}
                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        {/* Event Dates */}
                        {group.eventDates && group.eventDates.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <img 
                                src={calendarIcon} 
                                alt="Calendar" 
                                style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }} 
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    mb: 0.25,
                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                  }}
                                >
                                  Event Dates
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {formatDate(group.eventDates[0].start)} - {formatDate(group.eventDates[0].end)}
                                  {group.eventDates.length > 1 && ` (+${group.eventDates.length - 1})`}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Registration Dates */}
                        {group.registrationDates && group.registrationDates.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <img 
                                src={pencilIcon} 
                                alt="Registration" 
                                style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }} 
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    mb: 0.25,
                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                  }}
                                >
                                  Registration Dates
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {formatDate(group.registrationDates[0].start)} - {formatDate(group.registrationDates[0].end)}
                                  {group.registrationDates.length > 1 && ` (+${group.registrationDates.length - 1})`}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}

                        {/* Age Ranges */}
                        {group.ageRanges && group.ageRanges.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <img 
                                src={familyIcon} 
                                alt="Age Range" 
                                style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }} 
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    mb: 0.25,
                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                  }}
                                >
                                  Age Ranges
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {(() => {
                                    if (!group.ageRanges || group.ageRanges.length === 0) {
                                      return '';
                                    }
                                    
                                    // Find the lowest min and highest max across all age ranges
                                    let lowestMin = Infinity;
                                    let highestMax = -Infinity;
                                    
                                    group.ageRanges.forEach((a: any) => {
                                      if (a.min !== undefined && a.min < lowestMin) {
                                        lowestMin = a.min;
                                      }
                                      if (a.max !== undefined && a.max > highestMax) {
                                        highestMax = a.max;
                                      }
                                    });
                                    
                                    if (lowestMin === Infinity) {
                                      return '';
                                    }
                                    
                                    const minYears = Math.floor(lowestMin / 12);
                                    const minMonths = lowestMin % 12;
                                    
                                    if (highestMax !== -Infinity && highestMax !== lowestMin) {
                                      const maxYears = Math.floor(highestMax / 12);
                                      const maxMonths = highestMax % 12;
                                      return `${minYears}y ${minMonths}m - ${maxYears}y ${maxMonths}m`;
                                    }
                                    
                                    return `${minYears}y ${minMonths}m+`;
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Categories: aggregatedCategories from GET /groups, else from loaded events */}
                      {(() => {
                        const events = (group as any).events || [];
                        const aggregated = (group as any).aggregatedCategories as
                          | { name?: string }[]
                          | undefined;
                        const categoryNames = Array.from(
                          new Set(
                            (aggregated?.length
                              ? aggregated.map((c) => c.name)
                              : events.flatMap((event: any) =>
                                  (event.categoryRelationships || []).map(
                                    (rel: any) => rel.category?.name
                                  )
                                )
                            ).filter(Boolean)
                          )
                        ) as string[];

                        const mappedCategories = categoryNames
                          .map((name) =>
                            (Object.values(EventCategory) as string[]).find(
                              (value) => value === name
                            ) as EventCategory | undefined
                          )
                          .filter(Boolean) as EventCategory[];

                        if (!mappedCategories.length) return null;

                        return (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#1a1a1a',
                                mb: 0.75,
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                              }}
                            >
                              Categories
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {mappedCategories.map((category) => {
                                const IconComponent = categoryIcons[category];
                                const colors = categoryColors[category];
                                return (
                                  <Chip
                                    key={category}
                                    icon={<IconComponent />}
                                    label={category}
                                    size="small"
                                    sx={{
                                      backgroundColor: colors.light,
                                      color: colors.main,
                                      border: `1px solid ${colors.border}`,
                                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                                      height: '24px',
                                      '& .MuiChip-icon': {
                                        color: colors.main,
                                        fontSize: '14px',
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })()}

                      <Box
                        sx={{
                          mt: 'auto',
                          pt: 1.5,
                          borderTop: '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: groupColor,
                            fontWeight: 700,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                          }}
                        >
                          View Events →
                        </Typography>
                        {(() => {
                          const countFromApi =
                            (group as any).activeEventsCount ??
                            (group as any).eventsCount ??
                            (group as any).eventCount ??
                            null;
                          const derivedCount = Array.isArray((group as any).events)
                            ? (group as any).events.length
                            : null;
                          const eventCount =
                            (typeof countFromApi === 'number'
                              ? countFromApi
                              : derivedCount) ?? 0;
                          if (!eventCount) return null;
                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#666',
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                fontWeight: 500,
                              }}
                            >
                              {eventCount} {eventCount === 1 ? 'event' : 'events'}
                            </Typography>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HomePage;

